import { useState } from 'react'
import { Box, Group, Text, UnstyledButton } from '@mantine/core'
import { useFormat } from '../../_units'
import { useI18n } from '../../_i18n'
import { TILE } from '../_tiles'
import { WeekRow, WeekSection } from '../_week'
import { conditionLabel, isNightHour, precipConditionCode, thunderSignificant } from './weatherData'
import Caption, { RowUnit } from './Caption'
import { RowLabel, Stat } from './bandRow'
import { conditionGlyph } from './ConditionGlyph'
import DirectionLabel from './DirectionLabel'
import { CAPTION_LS, DATA_FZ, MONO_FONT, TILE_TEXT } from './railStyle'

const MUTED = TILE_TEXT.muted
const BORDER = '1px solid rgba(255,255,255,.14)'

// Current conditions, under the map. A headline temperature pair, then the
// same paired label/value rows the per-day weather band uses:
//
//   ☀️ CURRENT CONDITIONS
//              Clear
//        70°          71°
//        AIR         WATER
//   Chances      34% rain    40% thunder
//   Wind      9 kt ↗ SW      13 kt gusts
//   Seas     1.2' ↘ SE @ 6s
//   Visibility        8 nm
//   Pressure      1015 mb    62% humidity
//   NOAA/NWS FORECAST TEXT ▾
//
// This card came from the design handoff and was the last weather surface
// still on its own design: the only one following the site's light/dark
// theme, over a gradient, with a teal accent used nowhere else, its own type
// scale, and bordered tiles nested inside an already-bordered card. Five
// separate differences from the band below it, none carrying meaning. It now
// shares WeekSection, WeekRow, RowLabel/Stat, DirectionLabel and the TILE
// palette with the rest, so "now" and "today" are the same kind of object.
//
// DATA_FZ.headline is the one exception to the two-size scale, and this card
// is what it exists for — the same role NowTile's big time plays. Both
// temperatures take it rather than just air: on a tides app the water reading
// is one of the things people open the app for, and making it a footnote on
// the air temperature got the priority backwards.
export default function CurrentConditionsHero({ hour, timezone, forecasterText }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const [showText, setShowText] = useState(false)

  const wind = hour.wind || {}
  const seas = hour.seas || {}
  const night = isNightHour(hour.time, timezone)
  const storm = thunderSignificant(hour.thunder)

  const pp = hour.precip_probability_pct
  const ppShown = pp != null && Math.round(pp) > 0
  const thunderPct = hour.thunder?.probability_pct
  // A flagged (official forecaster) hour shows its number down to any real
  // risk; an unflagged one needs to clear >10%. Independent of
  // thunderSignificant, which only gates the glyph override at a higher bar.
  const thunderShown =
    thunderPct != null && (hour.thunder?.flagged ? thunderPct > 0 : thunderPct > 10)
  // A gust that rounds to the same number as the wind beside it says nothing.
  const gustsShown =
    hour.gusts_kt != null &&
    (wind.speed_kt == null || Math.round(hour.gusts_kt) > Math.round(wind.speed_kt))

  const rawText = forecasterText?.raw_text
  const issuer = forecasterText?.issuer

  return (
    <WeekSection
      bg={TILE.mid}
      emoji={conditionGlyph(storm ? 'thunderstorms' : hour.conditions, night, 17)}
      // "Marine weather · now", not "Current conditions". On a tidal-current
      // page that old title collided with the page's own vocabulary — Currents,
      // Last Current, Next Current — so "current conditions" read as the state
      // of the current rather than the state of the weather, which is a
      // perfectly plausible thing for a card under a currents map to show and
      // is not what this shows.
      //
      // Composed from two keys already translated in all 18 catalogs rather
      // than a new "marine weather now" string: a separator juxtaposes two
      // labels instead of forming a sentence, so there is no word order or
      // agreement to get wrong in any language, and no unreviewed translation.
      // It also ties this card to the per-day bands, which carry the same
      // t('weather') title — same subject, different span of time.
      title={`${t('weather')} · ${t('now')}`}
    >
      {/* Air and water as an equal pair, not a headline and its footnote.
        Water temp is a reading people open a tides app for, and it was
        previously the smallest thing in this block. Both take
        DATA_FZ.headline — the second number claims the exception the first
        already had rather than introducing a size — and each carries its own
        caption, because at this size neither number can say which it is.
        No divider between them: the gap and the two captions already
        separate the pair, and a vertical rule is a device used nowhere else
        on the page.

        The captions are the small dimmed Caption rather than the rows'
        full-size RowUnit. Under a 2.2rem number a 1.05rem white word would
        compete with the reading it labels; these sit outside the row system
        entirely, doing the same job a table's column heading does. */}
      <Box py={13} px={16} style={{ borderTop: BORDER, textAlign: 'center' }}>
        <Text fz={DATA_FZ.value} fw={700} style={{ color: TILE_TEXT.primary }}>
          {conditionLabel(t, storm ? 'thunderstorms' : hour.conditions)}
        </Text>
        <Group justify="center" gap={38} mt={9} wrap="wrap">
          {hour.air_temp_f != null && (
            <BigTemp value={fmt.temp(hour.air_temp_f)} label={t('air_temp')} />
          )}
          {hour.water_temp_f != null && (
            <BigTemp value={fmt.temp(hour.water_temp_f)} label={t('water_temp')} />
          )}
        </Group>
      </Box>

      {(ppShown || thunderShown) && (
        <WeekRow
          left={<RowLabel>{t('chances')}</RowLabel>}
          time={
            ppShown ? (
              <Stat
                value={fmt.percent(pp)}
                // Names what is actually falling, the same way the daily row
                // does. `conditions` is a bare code here rather than a day's
                // worth of them, so it stands in for both of
                // precipConditionCode's inputs.
                label={conditionLabel(
                  t,
                  precipConditionCode({ conditions_dominant: hour.conditions }),
                )}
              />
            ) : (
              <Stat value={fmt.percent(thunderPct)} label={t('weather_thunder')} />
            )
          }
          right={
            ppShown && thunderShown ? (
              <Stat value={fmt.percent(thunderPct)} label={t('weather_thunder')} />
            ) : null
          }
        />
      )}

      {wind.speed_kt != null && (
        <WeekRow
          left={<RowLabel>{t('wind')}</RowLabel>}
          time={
            <Group gap={4} align="center" wrap="nowrap">
              <Text fz={DATA_FZ.value} fw={700} style={{ color: TILE_TEXT.primary }}>
                {fmt.wind(wind.speed_kt)}
              </Text>
              <DirectionLabel deg={wind.direction_deg} />
            </Group>
          }
          right={gustsShown ? <Stat value={fmt.wind(hour.gusts_kt)} label={t('gusts')} /> : null}
        />
      )}

      {seas.height_ft != null && (
        <WeekRow
          left={<RowLabel>{t('seas')}</RowLabel>}
          time={
            <Group gap={4} align="center" wrap="nowrap">
              <Text fz={DATA_FZ.value} fw={700} style={{ color: TILE_TEXT.primary }}>
                {fmt.height(null, seas.height_ft)}
              </Text>
              <DirectionLabel deg={seas.direction_deg} />
              {seas.period_s != null && (
                <>
                  <RowUnit>{t('seas_period_at')}</RowUnit>
                  <Text fz={DATA_FZ.value} fw={700} style={{ color: TILE_TEXT.primary }}>
                    {t('wave_period_short', { period: Math.round(seas.period_s) })}
                  </Text>
                </>
              )}
            </Group>
          }
        />
      )}

      {hour.visibility_nm != null && (
        <WeekRow
          left={<RowLabel>{t('visibility')}</RowLabel>}
          time={
            <Text fz={DATA_FZ.value} fw={700} style={{ color: TILE_TEXT.primary }}>
              {fmt.visibility(hour.visibility_nm)}
            </Text>
          }
        />
      )}

      {(hour.pressure_mb != null || hour.humidity_pct != null) && (
        <WeekRow
          left={<RowLabel>{hour.pressure_mb != null ? t('pressure') : t('humidity')}</RowLabel>}
          time={
            hour.pressure_mb != null ? (
              <Text fz={DATA_FZ.value} fw={700} style={{ color: TILE_TEXT.primary }}>
                {fmt.pressure(hour.pressure_mb)}
              </Text>
            ) : (
              <Stat value={fmt.percent(hour.humidity_pct)} label={t('humidity')} />
            )
          }
          right={
            hour.pressure_mb != null && hour.humidity_pct != null ? (
              <Stat value={fmt.percent(hour.humidity_pct)} label={t('humidity')} />
            ) : null
          }
        />
      )}

      {rawText && (
        <Box style={{ borderTop: BORDER }}>
          <UnstyledButton
            onClick={() => setShowText((v) => !v)}
            style={{ display: 'block', width: '100%', padding: '11px 0' }}
          >
            <Group justify="center" gap={5}>
              <Text
                fz={DATA_FZ.label}
                fw={700}
                tt="uppercase"
                style={{ letterSpacing: CAPTION_LS, color: MUTED }}
              >
                {t('forecaster_text_link', { issuer })}
              </Text>
              <Text
                fz={DATA_FZ.value}
                lh={1}
                style={{
                  color: MUTED,
                  transform: showText ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform .18s ease',
                }}
              >
                ▾
              </Text>
            </Group>
          </UnstyledButton>

          {/*
            raw_text is rendered VERBATIM — no truncation, no paraphrase, no
            re-casing — for the same reason WarningsHead renders warning
            text verbatim: a reworded official marine forecast is a liability.
            Monospace and pre-wrap because the bulletin is written to a fixed
            column width and its line breaks carry meaning.

            Inset on TILE.dark like the wind chart, so the official words are
            visibly a quotation rather than our own copy, and capped in height
            so a seven-day bulletin can't push the map off screen.
          */}
          {showText && (
            <Box px={12} pb={14}>
              {forecasterText.issued_at && (
                <Caption>{fmt.dateTime(forecasterText.issued_at, timezone)}</Caption>
              )}
              <Box
                mt={6}
                px={12}
                py={10}
                style={{
                  background: TILE.dark,
                  borderRadius: 10,
                  maxHeight: 260,
                  overflow: 'auto',
                }}
              >
                <Text
                  component="pre"
                  fz="0.72rem"
                  style={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: MONO_FONT,
                    margin: 0,
                    lineHeight: 1.55,
                    color: 'rgba(255,255,255,.85)',
                  }}
                >
                  {rawText.trim()}
                </Text>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </WeekSection>
  )
}

// One of the pair of headline temperatures: the reading, then the word for
// which reading it is. Kept local — this is the only card in the app with a
// headline pair, and generalising it would invite a second one.
function BigTemp({ value, label }) {
  return (
    <Box>
      <Text
        fz={DATA_FZ.headline}
        fw={700}
        lh={1}
        style={{ color: TILE_TEXT.primary, letterSpacing: '-.02em' }}
      >
        {value}
      </Text>
      <Box mt={5}>
        <Caption>{label}</Caption>
      </Box>
    </Box>
  )
}
